import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { withStyles, Chip, IconButton, Menu, MenuItem, Typography } from '@material-ui/core';
import MoreVert from '@material-ui/icons/MoreVert';
import { getMimeLabel, formatFileSize, isImageMime, getThumbnailUrl } from '../utils';
import FileThumbnail from './FileThumbnail';
import RenameDialog from './RenameDialog';
import AssociationDialog from './AssociationDialog';
import DeleteDialog from './DeleteDialog';

const styles = (theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  thumbnailWrap: {
    marginRight: theme.spacing(1.5),
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.grey[500],
  },
  sharedBadge: {
    marginLeft: theme.spacing(1),
    height: 20,
  },
});

const FileRow = ({
  classes,
  file,
  cottageId,
  cottages,
  counts,
  maxCount,
  isUpdating,
  onView,
  onDownload,
  onRename,
  onReassign,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [associationOpen, setAssociationOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const prevUpdating = React.useRef(false);

  React.useEffect(() => {
    if (prevUpdating.current && !isUpdating) {
      setRenameOpen(false);
      setAssociationOpen(false);
      setDeleteOpen(false);
    }
    prevUpdating.current = isUpdating;
  }, [isUpdating]);

  const isShared = (file.listing_ids || []).length >= 2;
  const isImage = isImageMime(file.mime_type);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleRename = () => {
    handleMenuClose();
    setRenameOpen(true);
  };

  const handleReassign = () => {
    handleMenuClose();
    setAssociationOpen(true);
  };

  const handleDeleteOpen = () => {
    handleMenuClose();
    setDeleteOpen(true);
  };

  return (
    <div className={classes.row}>
      <div className={classes.thumbnailWrap}>
        <FileThumbnail
          mimeType={file.mime_type}
          src={isImage ? getThumbnailUrl(file.url) : undefined}
        />
      </div>
      <div className={classes.info}>
        <Typography
          variant="body2"
          className={classes.name}
          onClick={() => onView({ id: file.id, url: file.url })}
        >
          {file.display_name || file.filename}
        </Typography>
        <div className={classes.meta}>
          <Typography variant="caption" color="inherit">
            {getMimeLabel(file.mime_type)}
            {file.size_bytes ? ` - ${formatFileSize(file.size_bytes)}` : ''}
          </Typography>
          {isShared && (
            <Chip
              label={<FormattedMessage id="hostFiles.shared" defaultMessage="Multiple listings" />}
              size="small"
              variant="outlined"
              className={classes.sharedBadge}
            />
          )}
        </div>
      </div>
      <IconButton size="small" onClick={handleMenuOpen}>
        <MoreVert />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            onView({ id: file.id, url: file.url });
          }}
        >
          <FormattedMessage id="hostFiles.view" defaultMessage="Open" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            onDownload({ id: file.id, filename: file.display_name || file.filename });
          }}
        >
          <FormattedMessage id="hostFiles.download" defaultMessage="Download" />
        </MenuItem>
        <MenuItem onClick={handleRename}>
          <FormattedMessage id="hostFiles.rename" defaultMessage="Rename" />
        </MenuItem>
        <MenuItem onClick={handleReassign}>
          <FormattedMessage id="hostFiles.reassign" defaultMessage="Choose location" />
        </MenuItem>
        <MenuItem onClick={handleDeleteOpen}>
          <FormattedMessage id="Delete" defaultMessage="Delete" />
        </MenuItem>
      </Menu>

      <RenameDialog
        open={renameOpen}
        currentName={file.display_name || file.filename}
        isUpdating={isUpdating}
        onSave={(displayName) => {
          onRename({ id: file.id, display_name: displayName });
        }}
        onClose={() => setRenameOpen(false)}
      />

      <AssociationDialog
        open={associationOpen}
        file={file}
        cottages={cottages}
        counts={counts}
        maxCount={maxCount}
        isUpdating={isUpdating}
        onSave={(associations) => {
          onReassign({ id: file.id, ...associations });
        }}
        onClose={() => setAssociationOpen(false)}
      />

      <DeleteDialog
        open={deleteOpen}
        file={file}
        cottages={cottages}
        isUpdating={isUpdating}
        onDeleteAll={() => {
          onDelete({ id: file.id });
        }}
        onRemoveFromListing={() => {
          const remaining = (file.listing_ids || []).filter((id) => id !== cottageId);
          onReassign({ id: file.id, listing_ids: remaining });
        }}
        onClose={() => setDeleteOpen(false)}
      />
    </div>
  );
};

FileRow.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  file: PropTypes.shape({
    id: PropTypes.number.isRequired,
    display_name: PropTypes.string,
    filename: PropTypes.string,
    mime_type: PropTypes.string,
    url: PropTypes.string,
    size_bytes: PropTypes.number,
    listing_ids: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  cottageId: PropTypes.number.isRequired,
  cottages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  counts: PropTypes.shape({}),
  maxCount: PropTypes.number.isRequired,
  isUpdating: PropTypes.bool,
  onView: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onReassign: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default withStyles(styles)(FileRow);
